import React, { useCallback, useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { Application } from "src/domains/application/types";
import { useNotification } from "src/shell/contexts/notificationContext";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

import {
  listSecrets,
  upsertSecret,
  revealSecret,
  deleteSecret,
  listAppResources,
  type SecretMetadata,
  type ApplicationResourceRecord,
} from "../../../module/api/secretsApi";

// ── Types ─────────────────────────────────────────────────────────────────────

interface SecretsTabProps {
  system: Application;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SecretsTab({ system }: SecretsTabProps): React.ReactElement {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <StackSecretsSection system={system} />
      <ResourceSecretsSection system={system} />
    </Box>
  );
}

// ── Stack Secrets Section (CRUD) ──────────────────────────────────────────────

function StackSecretsSection({ system }: SecretsTabProps): React.ReactElement {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const [secrets, setSecrets] = useState<SecretMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealLoading, setRevealLoading] = useState<Set<string>>(new Set());

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string>("");
  const [formData, setFormData] = useState({ key: "", value: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchSecrets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSecrets(system.alias);
      setSecrets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load secrets");
    } finally {
      setLoading(false);
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchSecrets();
  }, [fetchSecrets]);

  const handleReveal = async (key: string) => {
    if (revealed[key]) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setRevealLoading((prev) => new Set(prev).add(key));
    try {
      const value = await revealSecret(system.alias, key);
      setRevealed((prev) => ({ ...prev, [key]: value }));
    } catch (err) {
      showNotification(
        `Failed to reveal secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setRevealLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleAdd = () => {
    setFormData({ key: "", value: "" });
    setAddDialogOpen(true);
  };

  const handleEdit = (secret: SecretMetadata) => {
    setActiveKey(secret.key);
    setFormData({ key: secret.key, value: "" });
    setEditDialogOpen(true);
  };

  const handleDelete = (key: string) => {
    setActiveKey(key);
    setDeleteDialogOpen(true);
  };

  const submitAdd = async () => {
    if (!formData.key.trim() || !formData.value.trim()) return;
    setSubmitting(true);
    try {
      await upsertSecret(system.alias, formData.key.trim(), formData.value);
      showNotification("Secret added successfully", "success");
      setAddDialogOpen(false);
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to add secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!formData.value.trim()) return;
    setSubmitting(true);
    try {
      await upsertSecret(system.alias, activeKey, formData.value);
      showNotification("Secret updated successfully", "success");
      setEditDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to update secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    setSubmitting(true);
    try {
      await deleteSecret(system.alias, activeKey);
      showNotification("Secret deleted successfully", "success");
      setDeleteDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to delete secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          Stack Secrets
        </Typography>
        {secrets.length > 0 && (
          <Chip label={secrets.length} size="small" variant="outlined" />
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" onClick={handleAdd}>
          Add Secret
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Custom secrets for this application. Values are encrypted in Vault and
        synced to your app at deploy time.
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : secrets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No secrets configured. Click "Add Secret" to add your first secret.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {secrets.map((secret) => (
            <Stack
              key={secret.key}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                px: 2,
                py: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <Typography
                variant="body2"
                fontFamily="monospace"
                fontWeight={600}
                sx={{ minWidth: 180, flexShrink: 0 }}
              >
                {secret.key}
              </Typography>
              <Typography
                variant="body2"
                fontFamily="monospace"
                color="text.secondary"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {revealed[secret.key] ?? "••••••••••••••••"}
              </Typography>
              <Chip
                label="secret"
                size="small"
                color="warning"
                variant="outlined"
              />
              <Tooltip
                title={revealed[secret.key] ? "Hide value" : "Reveal value"}
              >
                <IconButton
                  size="small"
                  onClick={() => handleReveal(secret.key)}
                  disabled={revealLoading.has(secret.key)}
                >
                  {revealLoading.has(secret.key) ? (
                    <CircularProgress size={16} />
                  ) : revealed[secret.key] ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(secret)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(secret.key)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}

      {/* Add Secret Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Secret</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Secret Key"
            placeholder="STRIPE_API_KEY"
            value={formData.key}
            onChange={(e) =>
              setFormData((p) => ({ ...p, key: e.target.value }))
            }
            fullWidth
            inputProps={{ pattern: "^[A-Z][A-Z0-9_]*$" }}
            helperText="UPPER_SNAKE_CASE. This is the env var name your app will receive."
          />
          <TextField
            margin="dense"
            label="Secret Value"
            placeholder="sk_test_..."
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Encrypted at rest in Vault. Never shown in list view."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdd}
            disabled={
              submitting || !formData.key.trim() || !formData.value.trim()
            }
          >
            {submitting ? <CircularProgress size={20} /> : "Add Secret"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Secret Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Secret</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Secret Key"
            value={activeKey}
            disabled
            fullWidth
          />
          <TextField
            autoFocus
            margin="dense"
            label="New Secret Value"
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Enter the new value. The previous value will be overwritten."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={submitting || !formData.value.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Secret</DialogTitle>
        <DialogContent>
          <Typography>
            Delete secret{" "}
            <strong style={{ fontFamily: "monospace" }}>{activeKey}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The secret will be removed from Vault
            and from your app's environment on the next deploy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={submitDelete}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Resource Secrets Section (read-only, redacted) ────────────────────────────

function ResourceSecretsSection({
  system,
}: SecretsTabProps): React.ReactElement | null {
  const [resources, setResources] = useState<ApplicationResourceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchResources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAppResources(system.alias);
      setResources(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load resource secrets",
      );
    } finally {
      setLoading(false);
    }
  }, [system.alias]);

  useEffect(() => {
    void fetchResources();
  }, [fetchResources]);

  const toggleExpand = (alias: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias);
      else next.add(alias);
      return next;
    });
  };

  /** Build the list of env vars that are auto-injected for a resource (redacted) */
  const getEnvVars = (
    resource: ApplicationResourceRecord,
  ): { name: string; redacted: boolean }[] => {
    const prefix = resource.alias.toUpperCase().replace(/-/g, "_");
    switch (resource.resource.resourceType) {
      case "postgres":
        return [
          { name: `${prefix}_DATABASE_URL`, redacted: true },
          { name: `${prefix}_DIRECT_URL`, redacted: true },
          { name: `${prefix}_DATABASE_USERNAME`, redacted: true },
          { name: `${prefix}_DATABASE_PASSWORD`, redacted: true },
          { name: `${prefix}_DATABASE_NAME`, redacted: true },
        ];
      case "nats":
        return [
          { name: `${prefix}_NATS_URL`, redacted: false },
          { name: `${prefix}_NATS_CREDS`, redacted: true },
        ];
      case "redis":
        return [{ name: `${prefix}_REDIS_URL`, redacted: false }];
      default:
        return [];
    }
  };

  // Don't render anything if no resources are attached
  if (!loading && !error && resources.length === 0) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          Resource Secrets
        </Typography>
        {resources.length > 0 && (
          <Chip label={resources.length} size="small" variant="outlined" />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Credentials automatically provisioned by managed resources attached to
        this application. Values are redacted — these are injected at deploy
        time.
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Stack spacing={1}>
          {resources.map((ar) => {
            const envVars = getEnvVars(ar);
            return (
              <Paper key={ar.id} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {ar.alias}
                      </Typography>
                      <Chip
                        label={ar.resource.resourceType}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      <Chip
                        label={ar.resource.status}
                        size="small"
                        color={
                          ar.resource.status === "running"
                            ? "success"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {ar.resource.alias}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => toggleExpand(ar.alias)}>
                    {expanded.has(ar.alias) ? "Hide" : "Show"} env vars
                  </Button>
                </Stack>
                <Collapse in={expanded.has(ar.alias)}>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {envVars.map((envVar) => (
                      <Stack
                        key={envVar.name}
                        direction="row"
                        spacing={2}
                        sx={{ py: 0.25 }}
                      >
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            minWidth: 280,
                          }}
                        >
                          {envVar.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          color="text.secondary"
                        >
                          {envVar.redacted
                            ? "••••••••••••••••"
                            : "(auto-injected)"}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default SecretsTab;
